import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const doc = event.detail.data.html;
    const productId = this.dataset.productId;
    /** @type {Element | null} */
    let newProductPrice = null;
    if (productId) {
      newProductPrice = doc.querySelector(`product-price.price-block[data-product-id="${productId}"]`);
      if (!newProductPrice) {
        newProductPrice = doc.querySelector(`product-price[data-product-id="${productId}"]`);
      }
    }
    if (!newProductPrice) {
      newProductPrice = doc.querySelector('product-price');
    }
    if (!newProductPrice) return;

    this.#syncOfferBadge(doc);
    this.#replacePriceContent(newProductPrice);
  };

  /**
   * Copy price HTML. Never morph, and never write empty markup — product-card
   * used to morph first, which moved nodes out of the fetched document so this
   * listener wiped the live PLP price.
   * @param {Element} newProductPrice
   */
  #replacePriceContent(newProductPrice) {
    const nextSelected = newProductPrice.querySelector('.price-block__cx-selected-price');
    const nextDefault = newProductPrice.querySelector('.price-block__default-price');
    const nextPriceHtml = (
      nextSelected?.innerHTML ||
      nextDefault?.innerHTML ||
      newProductPrice.innerHTML
    ).trim();

    if (!nextPriceHtml) return;

    const isCxSlider = Boolean(this.closest('.cx-product-slider'));
    const currentSelected = this.querySelector('.price-block__cx-selected-price');
    const currentDefault = this.querySelector('.price-block__default-price');

    if (isCxSlider && currentSelected) {
      currentSelected.innerHTML = nextSelected?.innerHTML?.trim() || nextPriceHtml;
      return;
    }

    if (currentDefault) {
      currentDefault.innerHTML = nextPriceHtml;
      if (currentSelected) currentSelected.innerHTML = nextPriceHtml;
      return;
    }

    if (this.innerHTML !== newProductPrice.innerHTML) {
      this.innerHTML = newProductPrice.innerHTML;
    }
  }

  /**
   * Syncs the offer badge sibling when it lives outside product-price (price block).
   * @param {Document} doc - Parsed section HTML from the variant update.
   */
  #syncOfferBadge(doc) {
    const group = this.closest('.price-block-group');
    const blockId = group?.dataset.priceBlockId || this.dataset.blockId;
    if (!group || !blockId) return;

    const newGroup = doc.querySelector(`.price-block-group[data-price-block-id="${blockId}"]`);
    if (!newGroup) return;
    group.dataset.offerPosition = newGroup.dataset.offerPosition || 'inline';

    const currentBadge = group.querySelector(':scope > .product-offer-badges');
    const newBadge = newGroup.querySelector(':scope > .product-offer-badges');

    if (newBadge) {
      if (currentBadge) {
        currentBadge.outerHTML = newBadge.outerHTML;
      } else {
        this.insertAdjacentHTML('beforebegin', newBadge.outerHTML);
      }
    } else if (currentBadge) {
      currentBadge.remove();
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

/** A product promotion deadline stays fixed through variant changes and page reloads. */
class ProductOfferCountdown extends HTMLElement {
  #timer;

  connectedCallback() {
    clearInterval(this.#timer);
    const raw = this.dataset.endsAt || '';
    // Require an explicit timezone so every shopper sees the same deadline.
    const end = /T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(raw) ? Date.parse(raw) : NaN;
    const update = () => {
      const seconds = Math.ceil((end - Date.now()) / 1000);
      if (!Number.isFinite(seconds) || seconds <= 0) {
        this.hidden = true;
        clearInterval(this.#timer);
        return false;
      }
      const days = Math.floor(seconds / 86400);
      const hours = String(Math.floor(seconds % 86400 / 3600)).padStart(2, '0');
      const minutes = String(Math.floor(seconds % 3600 / 60)).padStart(2, '0');
      const remainder = String(seconds % 60).padStart(2, '0');
      this.textContent = `${this.dataset.label || 'Offer ends in'} ${days ? `${days}d ` : ''}${hours}h ${minutes}m ${remainder}s`;
      this.hidden = false;
      return true;
    };
    if (update()) this.#timer = setInterval(update, 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }
}

if (!customElements.get('product-offer-countdown')) {
  customElements.define('product-offer-countdown', ProductOfferCountdown);
}
